export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth endpoints must NOT trigger auto-logout (a 401 there means bad
// credentials / expected verification failure, not an expired session).
const isAuthEndpoint = (url = '') =>
  /\/auth\/(login|register|logout|me|refresh|forgot-password|reset-password)/.test(url || '');

let handlingUnauthorized = false;

// Called whenever an authenticated request comes back 401.
// To avoid logging users out mid-work on a stray/endpoint-specific 401 or a
// transient connection issue, we FIRST verify the session via /auth/me:
//   - /auth/me OK      -> token still valid, keep the session (do nothing)
//   - /auth/me 401     -> token genuinely dead -> clear + redirect to login
//   - network/error    -> connection issue -> keep the session (do nothing)
export const handleUnauthorized = async (url = '') => {
  if (isAuthEndpoint(url)) return;
  const token = localStorage.getItem('auth_token');
  if (!token) return;
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;
  try {
    const res = await fetch(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status !== 401) {
      // Token still valid (200) or a non-auth error — this was not a real
      // session expiry, so do NOT log the user out.
      handlingUnauthorized = false;
      return;
    }
  } catch (e) {
    // Network / connection issue — never log out on this.
    handlingUnauthorized = false;
    return;
  }
  // Session genuinely expired/invalid -> log out.
  localStorage.removeItem('auth_token');
  try { sessionStorage.setItem('session_expired', '1'); } catch (e) { /* ignore */ }
  window.location.href = '/';
};

// Build headers that always include the current auth token (if present).
export const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('auth_token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

// Central fetch wrapper: always attaches the auth token so no request can
// silently miss authentication, and auto-logs-out on an expired-token 401.
export const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  const headers = { ...(options.headers || {}) };
  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    handleUnauthorized(url);
  }
  return response;
};
